import { productDiscoverPath } from "@/components/widgets/product-carousel-query";
import { normalizeFilterOptions } from "@/features/instantsearch/services/filter-normalizer";

/**
 * The result group a chat "take me to Explore" should continue, as the
 * backend sends it on the redirection event (`exploreContext`).
 *
 * Read defensively: it arrives over the stream and an older backend sends
 * none, which must keep the plain browsing redirect working.
 */
export interface ExploreRedirectContext {
  productIds?: unknown;
  filterOptions?: unknown;
  discoverQuery?: unknown;
}

export interface ExploreRedirectTarget {
  url: string;
  state: { filters: Record<string, string[]>; fromChat: true };
}

/**
 * The Discover route for that group: the same route and navigation state the
 * product strip's own "Discover more" button builds, so a chat redirect and a
 * click land on the same screen. `null` means there is no usable group and the
 * caller keeps plain browsing.
 */
export function exploreRedirectTarget(
  context: ExploreRedirectContext | null | undefined,
): ExploreRedirectTarget | null {
  if (!context || typeof context !== "object") return null;
  const productIds = Array.isArray(context.productIds)
    ? context.productIds.filter(
        (id) => typeof id === "string" && id.trim() !== "",
      )
    : [];
  if (productIds.length === 0) return null;
  if (!context.filterOptions || typeof context.filterOptions !== "object") {
    return null;
  }

  const filters = normalizeFilterOptions(
    context.filterOptions as Parameters<typeof normalizeFilterOptions>[0],
  );
  const discoverQuery =
    typeof context.discoverQuery === "string" ? context.discoverQuery : undefined;
  return {
    url: productDiscoverPath(filters, discoverQuery),
    state: { filters, fromChat: true },
  };
}
